import * as React from 'react';
import * as autoBind from 'react-autobind';
import * as classNames from 'classnames';
import accepts from 'attr-accept';
import DropMediaGlyph from '../../assets/DropMediaGlyph.svg';
import UserMessaging from '../../utils/UserMessaging';
import {ControlButton} from '@techsmith/tsc-cloud-style-guide';
import Strings from '../../globals/Strings';
import Themeable from '../../types/Themeable';

const ACCEPTED_FILE_TYPE = 'video/mp4,image/png,image/jpeg';

export default class LocalVideoImporter extends React.Component<LocalVideoImporterProps, LocalVideoImporterState> {
   readonly state: LocalVideoImporterState = {dropZoneState: 'normal'};
   filePickerRef: HTMLInputElement;

   constructor(props: LocalVideoImporterProps) {
      super(props);
      autoBind(this);
   }

   onFilePickerChange(event: any): void {
      this.onFilesSelect(event.target.files);
   }

   onFilesSelect(files: any[]): void {
      if (!this.filesShouldBeAccepted(files)) {
         UserMessaging.warn('Please select a single MP4, JPG or PNG file');
         return;
      }

      this.props.onFileSelect(files[0]);
   }

   filesShouldBeAccepted(files: any[]): boolean {
      return files.length === 1 && accepts(files[0], ACCEPTED_FILE_TYPE);
   }

   onDragEnter(e: any): void {
      e.preventDefault();
      const draggedFiles = e.dataTransfer && e.dataTransfer.items;

      const areUnknownFiles = (files: any[]): boolean => {
         if (!files) {
            return true;
         }

         const file = draggedFiles[0];
         return file && file.type === 'application/x-moz-file';
      };

      let newDropZoneState: string;
      if (areUnknownFiles(draggedFiles)) {
         newDropZoneState = 'unknown-file';
      } else {
         newDropZoneState = this.filesShouldBeAccepted(draggedFiles) ? 'accepted-file' : 'rejected-file';
      }

      this.setState({dropZoneState: newDropZoneState});
   }

   onDragLeave(e: any): void {
      e.preventDefault();
      this.setState({dropZoneState: 'normal'});
   }

   onDrop(e: any): void {
      e.preventDefault();
      this.setState({dropZoneState: 'normal'});
      this.onFilesSelect(e.dataTransfer.files);
   }

   onDragOver(e: any): boolean {
      e.preventDefault();
      e.stopPropagation();
      try {
         e.dataTransfer.dropEffect = 'copy';
      } catch (err) {
         // oh well
      }
      return false;
   }

   simulateFilePickerClick() {
      this.filePickerRef.click();
   }

   setFilePickerRef(ref: HTMLInputElement) {
      this.filePickerRef = ref;
   }

   render() {
      const dropZoneClasses = classNames('drag-drop-media-container', this.state.dropZoneState);

      return (
         <div className="review-modal-body">
            <div className={dropZoneClasses} onClick={this.simulateFilePickerClick} onDragOver={this.onDragOver} onDrop={this.onDrop}
               onDragEnter={this.onDragEnter} onDragLeave={this.onDragLeave}>
               <div className="drop-media-glyph"><DropMediaGlyph /></div>
               <div className="drag-drop-text">{Strings.labels.fileImporter.dragAndDropUpload}</div>
            </div>
            <div className="or-container">or</div>
            <div className="file-importer-button">
               <input type="file" name="file" id="local-importer-file-selector" className="hidden-file-input"
                  onChange={this.onFilePickerChange} accept={ACCEPTED_FILE_TYPE} ref={this.setFilePickerRef} tabIndex={-1} />
               <label className="hidden-file-input-label" htmlFor="local-importer-file-selector">{Strings.labels.fileImporter.selectUploadFile}</label>
               <ControlButton label={Strings.labels.fileImporter.selectUploadFile}
                  onClick={this.simulateFilePickerClick}
                  themeName={this.props.themeName}
                  testId="local-video-importer-button" />
            </div>
         </div>
      );
   }
}

interface LocalVideoImporterProps extends Themeable {
   onFileSelect(file: File): void;
}

interface LocalVideoImporterState {
   dropZoneState: string;
}
